import { TwoUIProgressViewOperationData, useTwoUIComponentQos, useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { getURLDomainName } from "@amx/ux-twoui-utils";
import { ImageLoadState, IPersonaCoinProps, mergeStyles, Persona as FluentPersona, PersonaSize as FluentPersonaSize } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import React, { useEffect, useState } from "react";
import { useComponentLogger } from "../../shared/logging";
import { AriaLabelRejectProps, validateProps } from "../../shared/props";
import { ShimmerCircle } from "../Shimmer/ShimmerCircle/ShimmerCircle";
import { ShimmerProgressView } from "../Shimmer/ShimmerProgressView/ShimmerProgressView";

export const imageLoadComponentPrefix = "persona.image-load.";

export type PersonaSize = "24" | "28" | "32" | "40" | "48" | "56" | "72" | "100" | "120" | "160" | "200" | "256";

export interface PersonaProps extends AriaLabelRejectProps {
    /**
     * Operation data to show in progress state for Persona when image url is
     * being acquired.
     */
    readonly operationData?: TwoUIProgressViewOperationData;
    /**
     * Size of the persona in pixels
     */
    readonly size: PersonaSize;

    /**
     * URL of the image to use
     */
    readonly imageUrl?: string;

    /**
     * Primary text to display, usually the person's name
     */
    readonly text?: string;

    /**
     * Secondary text to display, usually the person's role (or could be email)
     */
    readonly secondaryText?: string;

    /**
     * Whether to hide the text of the persona. Defaults to false
     */
    readonly hideText?: boolean;

    /**
     * Only track image load when tracking id is provided.
     */
    readonly trackingId?: string;
}

const customCoinSizes = ["160", "200", "256"] as const;
export type CustomCoinSize = typeof customCoinSizes[number];

const fabricPersonaSizeMapping: { [P in Exclude<PersonaSize, CustomCoinSize>]: FluentPersonaSize } = {
    "24": FluentPersonaSize.size24,
    "28": FluentPersonaSize.size28,
    "32": FluentPersonaSize.size32,
    "40": FluentPersonaSize.size40,
    "48": FluentPersonaSize.size48,
    "56": FluentPersonaSize.size56,
    "72": FluentPersonaSize.size72,
    "100": FluentPersonaSize.size100,
    "120": FluentPersonaSize.size120
};

export function fluentPersonaSizeFromPx(size: PersonaSize): FluentPersonaSize {
    return fabricPersonaSizeMapping[size || "32"];
}
// Type guard that returns a type predicate checking whether 'size' is of CustomCoinSize type
// see https://www.typescriptlang.org/docs/handbook/advanced-types.html for more about type predicates
export const checkCustomCoinSize = (size: PersonaSize): size is CustomCoinSize => {
    return customCoinSizes.includes(size as any);
};
export const Persona: React.FC<PersonaProps> = ({
    operationData,
    size = "32",
    imageUrl,
    text,
    secondaryText,
    hideText = false,
    trackingId,
    ...otherProps
}) => {
    validateProps("Persona", otherProps);
    const componentInstantId = useId();
    const twoUITelemetry = useTwoUITelemetry();
    useComponentLogger("Persona", otherProps.id);
    const { startOperation, reportSuccess, reportFailure } = useTwoUIComponentQos();

    const [showShimmer, setShowShimmer] = useState(false);

    const isCustomCoinSize = checkCustomCoinSize(size);

    const computedSize = !isCustomCoinSize ? fluentPersonaSizeFromPx(size) : FluentPersonaSize.size120;
    const computedCoinSize = isCustomCoinSize ? Number(size) : undefined;

    const imageDomain = !!imageUrl ? getURLDomainName(imageUrl) : "unknown-domain";
    const componentNameForImageTracking = `${imageLoadComponentPrefix}${imageDomain}.${trackingId}`;

    const theme = useTheme();

    useEffect(() => {
        twoUITelemetry.checkUrlAndReportHttp(imageUrl);
        if (!!imageUrl) {
            // When URL is there, image start loading from internet. The shimmer
            // is for that experience.
            setShowShimmer(true);
            if (trackingId) {
                startOperation(componentNameForImageTracking, componentInstantId, true);
            }
        } else {
            // new empty image url so we hide the shimmer.
            setShowShimmer(false);
        }
    }, [imageUrl]);

    const onPhotoLoadingStateChange = (newImageLoadState: ImageLoadState) => {
        if (newImageLoadState === ImageLoadState.error) {
            if (trackingId) {
                reportFailure(componentNameForImageTracking, componentInstantId, new Error("Image failed to load"));
            }
            setShowShimmer(false);
        } else if (newImageLoadState === ImageLoadState.loaded) {
            if (trackingId) {
                reportSuccess(componentNameForImageTracking, componentInstantId);
            }
            setShowShimmer(false);
        }
    };

    const onRenderInitials = () => {
        return <ShimmerCircle height={+size} />;
    };

    const getFontOverride = mergeStyles({
        selectors: {
            ["span"]: {
                fontSize: "96px",
            },
        }
    });
    const coinProps: IPersonaCoinProps | undefined = size === "256" ? {
        coinProps: {
            className: getFontOverride
        }
    } : undefined;

    const baseFluentPersonaProps = {
        size: computedSize,
        coinSize: computedCoinSize,
        imageUrl: imageUrl,
        text: text,
        secondaryText: secondaryText,
        hidePersonaDetails: hideText,
        onPhotoLoadingStateChange: onPhotoLoadingStateChange,
        // When showShimmer is set to be true, we will render ShimmerCircle.
        // When undefined is passed it will resume the default.
        onRenderInitials: showShimmer ? onRenderInitials : undefined,
        initialsTextColor: theme.palette.neutralHigh,
        showInitialsUntilImageLoads: showShimmer,
        coinProps: coinProps,
        ...otherProps
    };

    const baseComponent = (
        <FluentPersona
            {...baseFluentPersonaProps}
        />
    );

    const shimmeringPersona = (
        <FluentPersona
            {...baseFluentPersonaProps}
            showInitialsUntilImageLoads={true}
            onRenderInitials={onRenderInitials}
            onPhotoLoadingStateChange={undefined}
        />
    );

    if (operationData) {
        return (
            <ShimmerProgressView
                operationData={operationData}
                inertView={shimmeringPersona}
                firstLoadView={shimmeringPersona}
                updatingView={shimmeringPersona}
                updateFailedView={baseComponent}
                finishedView={baseComponent}
                failedView={baseComponent}
            />
        );
    }

    return baseComponent;
};
